import {
  BarChart3,
  Blocks,
  BookOpen,
  Bot,
  Compass,
  FileSearch,
  Globe2,
  LineChart,
  Megaphone,
  MessageSquareText,
  Search,
  Target,
  Workflow,
} from "lucide-react";

export const navItems = [
  { href: "/", label: "首页" },
  { href: "/services", label: "服务" },
  { href: "/cases", label: "案例" },
  { href: "/solutions", label: "解决方案" },
  { href: "/pricing", label: "套餐价格" },
  { href: "/insights", label: "增长洞察" },
  { href: "/about", label: "关于我们" },
  { href: "/workspace", label: "工作台" },
];

export const services = [
  {
    slug: "brand-positioning",
    icon: Compass,
    title: "品牌与产品表达",
    description:
      "围绕目标客户、业务优势与应用场景，建立经得起比较的品牌叙事与产品价值表达。",
    items: [
      "品牌定位与价值主张",
      "目标客户与决策洞察",
      "产品卖点与解决方案",
      "品牌语言与内容规范",
    ],
  },
  {
    slug: "website-development",
    icon: Blocks,
    title: "官网与独立站",
    description:
      "建设专业、可信且可运营的官网或独立站，让产品、内容、案例与咨询入口形成完整阵地。",
    items: [
      "信息架构与客户路径",
      "界面体验与信任体系",
      "前端开发与 CMS",
      "数据监测与线索承接",
    ],
  },
  {
    slug: "content-marketing",
    icon: BookOpen,
    title: "内容增长",
    description:
      "将产品能力、客户案例与行业经验沉淀为可检索、可传播、可持续复用的内容资产。",
    items: [
      "内容策略与主题规划",
      "产品、案例与场景内容",
      "行业洞察与采购指南",
      "内容发布与渠道分发",
    ],
  },
  {
    slug: "seo-geo",
    icon: Search,
    title: "SEO / GEO",
    description:
      "围绕客户搜索需求建立传统搜索与 AI 搜索可见性，使品牌更容易被发现、理解和信任。",
    items: [
      "关键词与搜索需求研究",
      "技术 SEO 与站点结构",
      "主题内容与页面优化",
      "GEO 与 AI 搜索布局",
    ],
  },
  {
    slug: "market-growth",
    icon: LineChart,
    title: "市场增长",
    description:
      "从目标市场、竞争格局与客户需求出发，制定可执行的增长优先级与渠道协同计划。",
    items: [
      "市场、客户与竞品洞察",
      "增长目标与阶段策略",
      "渠道协同与传播计划",
      "数据跟踪与阶段复盘",
    ],
  },
  {
    slug: "lead-operations",
    icon: Workflow,
    title: "线索与持续运营",
    description:
      "完善表单、邮件、即时通讯与来源追踪，让每一次获客动作都能被承接、跟进和优化。",
    items: [
      "咨询入口与询盘表单",
      "渠道来源与线索识别",
      "内容与页面运营节奏",
      "数据驾驶舱与复盘",
    ],
  },
];

export const solutionCards = [
  {
    slug: "domestic-brand-growth",
    number: "01",
    title: "国内品牌增长",
    description:
      "适合产品有竞争力，但品牌表达分散、官网老旧，仍主要依赖销售和转介绍获客的企业。先统一市场表达，再建设官网、内容、搜索与咨询承接。",
    image: "/assets/city.jpg",
    tags: ["统一品牌与产品口径", "重构官网决策路径", "建立内容主题体系", "接入搜索与线索数据"],
    details: [
      "盘点产品、客户和销售资料，明确目标客户、采购关注点、差异化价值与证据，形成官网、内容和销售能够共用的一套表达。",
      "按客户从了解、比较到咨询的顺序重组核心页面，把产品能力、应用场景、企业实力和联系入口放到正确的位置。",
      "围绕产品问题、行业场景和采购决策建立主题地图、内容模板与发布节奏，避免内容更新长期依赖临时选题。",
      "配置搜索基础、访问来源、重点页面行为和咨询入口记录，用真实访问与销售反馈决定下一阶段优化内容。",
    ],
  },
  {
    slug: "product-global-expansion",
    number: "02",
    title: "产品出海增长",
    description:
      "适合已有产品和交付能力，但尚未形成目标市场判断、海外表达、独立站和可追踪询盘路径的制造与外贸企业。",
    image: "/assets/earth.jpg",
    tags: ["明确市场与产品优先级", "建立海外产品表达", "建设搜索内容基础", "打通海外询盘链路"],
    details: [
      "先确定目标国家、重点产品、客户角色与竞争环境，避免一开始同时铺开过多市场、语言和产品线。",
      "把规格、工艺、认证、交付与服务能力转化为海外采购者能理解和核验的产品价值、应用场景与信任信息。",
      "规划独立站核心页面、Google 关键词、AI 搜索问题与多语言内容，完成技术 SEO 和结构化数据基础。",
      "统一表单、邮件和 WhatsApp 等联系入口并记录来源；企业销售团队负责及时跟进并反馈线索质量。",
    ],
  },
  {
    slug: "corporate-website-growth",
    number: "03",
    title: "企业官网增长",
    description:
      "适合官网无法讲清产品价值、内容难维护或咨询路径不明确的企业。重点不是换一套视觉，而是重建客户理解与行动路径。",
    image: "/assets/laptop.jpg",
    tags: ["诊断现有网站断点", "重组核心页面结构", "配置 CMS 与移动端", "完善数据与咨询入口"],
    details: [
      "检查首页、产品页、导航、内容维护和咨询入口，找出客户看不懂、找不到或无法继续行动的位置。",
      "围绕首页、产品、解决方案、案例与 FAQ 等核心任务重组页面，标准方案按合同约定新增或重构 8–12 个核心页面。",
      "完成响应式前端、CMS 内容结构和移动端适配，让企业能够自行维护产品、案例和洞察内容。",
      "配置访问分析、来源记录、表单与联系入口，为后续页面优化和线索复盘提供统一数据口径。",
    ],
  },
  {
    slug: "global-website-growth",
    number: "04",
    title: "海外独立站增长",
    description:
      "适合已有出口业务或平台订单，希望建立自主海外品牌和客户渠道的企业。独立站从目标市场与采购决策出发，而不是直接翻译国内官网。",
    image: "/assets/camera.jpg",
    tags: ["规划市场与站点范围", "补齐采购信任信息", "本地化页面与搜索内容", "追踪询盘来源"],
    details: [
      "根据目标国家、产品优先级、语言与团队维护能力确定首期站点范围，避免多语言页面建成后无人更新。",
      "围绕规格、认证、质量控制、交付、服务和常见采购问题组织页面，让海外客户能够判断是否值得询盘。",
      "按当地搜索语境重写产品、场景、FAQ 和品牌内容，建立 Google SEO、结构化数据与 AI 搜索问题基础。",
      "连接表单、邮箱、WhatsApp 与来源记录，区分不同市场、页面和内容带来的询盘，并进入周期复盘。",
    ],
  },
  {
    slug: "manufacturing-growth",
    number: "05",
    title: "制造企业增长",
    description:
      "适合产品、工艺和交付能力强，但线上资料仍以参数表、画册和零散文件为主的制造企业。将专业能力转成客户可理解、可检索的内容。",
    image: "/assets/factory.jpg",
    tags: ["梳理产品与应用体系", "转化工艺和交付优势", "建设选型与场景内容", "连接询盘与销售反馈"],
    details: [
      "统一产品分类、型号关系、关键参数和应用场景，解决客户找不到合适产品、销售重复解释的问题。",
      "把工艺、质量控制、供应链与交付能力放进具体客户任务中表达，并明确可提供的认证、样品和资料。",
      "围绕选型、对比、安装、使用与常见问题建立产品页、场景页和专业内容，支持搜索与销售沟通。",
      "统一咨询、选型和资料获取入口，记录来源并结合销售反馈判断客户质量和下一期内容重点。",
    ],
  },
];

export const cases = [
  {
    category: "国内品牌增长",
    title: "企业品牌表达与官网升级",
    subtitle:
      "帮助品牌表达不清、官网老旧或缺少持续内容运营的企业，建立更清晰的品牌表达和获客基础。",
    image: "/assets/city.jpg",
    deliverables: ["品牌表达系统", "官网核心页面", "内容与数据基础"],
    facts: [
      { label: "适合谁", value: "品牌表达不清、官网老旧、市场认知不足的企业" },
      { label: "推进路径", value: "品牌梳理 → 官网与内容建设 → 流量与用户承接" },
      { label: "验收内容", value: "品牌与产品口径、核心页面、内容和数据配置" },
    ],
  },
  {
    category: "企业出海",
    title: "制造与外贸企业产品出海",
    subtitle:
      "面向有产品优势但缺少海外品牌、独立站和询盘承接路径的制造与外贸企业。",
    image: "/assets/earth.jpg",
    deliverables: ["海外独立站", "Google / AI 搜索", "询盘承接"],
    facts: [
      { label: "适合谁", value: "制造、工厂、外贸、工业品和创新产品企业" },
      { label: "推进路径", value: "产品 → 海外市场 → 独立站 → 搜索流量 → 询盘" },
      { label: "验收内容", value: "产品表达、独立站、搜索基础、询盘入口和数据配置" },
    ],
  },
  {
    category: "P02 深度增长诊断",
    title: "先找准问题，再决定怎么投入",
    subtitle:
      "适合已有一定基础，希望先解决当前短板、掌握后续方法，再决定是否持续投入的企业。",
    image: "/assets/laptop.jpg",
    deliverables: ["基线诊断", "90 天行动清单", "工具交接"],
    facts: [
      { label: "标准周期", value: "5–7 个工作日" },
      { label: "直接处理", value: "优化 1 项最影响客户理解的核心问题" },
      { label: "明确交付", value: "10 个选题、2 份模板、关键词方向和增长看板模板" },
    ],
  },
  {
    category: "P04 标准增长包",
    title: "12 周建立品牌与获客基础",
    subtitle:
      "适合需要系统建立品牌表达、官网、内容、搜索和数据监测基础的企业。",
    image: "/assets/office.jpg",
    deliverables: ["品牌表达", "8–12 个核心页面", "SEO / GEO 内容"],
    facts: [
      { label: "标准周期", value: "12 周" },
      { label: "网站交付", value: "新增或重构 8–12 个核心页面，配置 CMS 和移动端" },
      { label: "数据交付", value: "配置 GA4、GSC、Bing，并提供 3 次增长复盘" },
    ],
  },
  {
    category: "P05 持续增长运营",
    title: "基础建成后的持续内容与优化",
    subtitle:
      "面向已完成基础建设，需要持续更新内容、优化重点页面并按月复盘数据的企业。",
    image: "/assets/factory.jpg",
    deliverables: ["月度规划", "持续内容输出", "页面迭代"],
    facts: [
      { label: "合作周期", value: "6 个月起" },
      { label: "月度内容", value: "每月约 100 篇 / 条站内外内容资产" },
      { label: "月度优化", value: "更新 2 个重点页面，并提交增长看板和复盘" },
    ],
  },
  {
    category: "P06 企业定制版",
    title: "多产品、多市场与多语言增长",
    subtitle:
      "适合产品线、目标市场、语言或站点较多，需要复杂增长系统建设的企业。",
    image: "/assets/camera.jpg",
    deliverables: ["多产品", "多市场", "多语言 / 多站点"],
    facts: [
      { label: "适合谁", value: "存在多产品、多市场、多语言或复杂系统需求的企业" },
      { label: "建议周期", value: "3–6 个月起" },
      { label: "服务边界", value: "范围、验收标准和第三方费用以项目合同为准" },
    ],
  },
];

export const insights = [
  {
    category: "行业观察",
    title: "从流量采购到资产经营：企业增长系统正在重构",
    date: "2026-07-18",
    minutes: "12 分钟",
    image: "/assets/earth.jpg",
  },
  {
    category: "国内品牌增长",
    title: "企业品牌增长的起点：把产品能力转化为市场可理解的价值",
    date: "2026-07-12",
    minutes: "8 分钟",
    image: "/assets/city.jpg",
  },
  {
    category: "企业出海",
    title: "中国企业产品出海：从独立站阵地到海外询盘的关键路径",
    date: "2026-07-08",
    minutes: "10 分钟",
    image: "/assets/factory.jpg",
  },
  {
    category: "官网与独立站",
    title: "官网不只是展示页：如何承接企业客户的理解、信任与咨询",
    date: "2026-07-02",
    minutes: "9 分钟",
    image: "/assets/laptop.jpg",
  },
  {
    category: "SEO / GEO",
    title: "SEO 与 GEO 如何协同：让品牌同时进入搜索结果与 AI 回答",
    date: "2026-06-28",
    minutes: "7 分钟",
    image: "/assets/SEO与GEO优化图.png",
  },
  {
    category: "内容增长",
    title: "从一次发布到长期复利：企业内容资产的规划与运营方法",
    date: "2026-06-21",
    minutes: "6 分钟",
    image: "/assets/team.jpg",
  },
];

export const method = [
  {
    icon: FileSearch,
    number: "01",
    title: "诊断与洞察",
    text: "理解企业、产品、目标客户与市场环境，识别最影响增长的关键断点。",
  },
  {
    icon: Target,
    number: "02",
    title: "策略与规划",
    text: "明确增长目标、核心路径、资产范围、衡量方式与阶段优先级。",
  },
  {
    icon: Megaphone,
    number: "03",
    title: "建设与运营",
    text: "按阶段建设品牌表达、官网、内容、搜索基础与线索承接系统。",
  },
  {
    icon: BarChart3,
    number: "04",
    title: "复盘与沉淀",
    text: "以数据和业务反馈持续校准，把有效动作沉淀为可复用的增长资产。",
  },
];

export const aboutValues = [
  {
    icon: Bot,
    title: "AI 增强效率",
    text: "以 AI 辅助研究、内容生产与数据分析，把更多时间用于策略判断与质量把控。",
  },
  {
    icon: MessageSquareText,
    title: "专业协同交付",
    text: "品牌、内容、设计、开发与增长成员按项目阶段协作，对关键决策与交付质量负责。",
  },
  {
    icon: Globe2,
    title: "建设长期资产",
    text: "将品牌、官网、内容、搜索与线索连接为企业自主掌握、持续积累的增长基础。",
  },
];
